// Default landing page — which surface the app opens on when the
// user arrives at the root URL. Stored in localStorage rather than
// the user record because the preference is naturally per-device
// (you might want Schedule on desktop and Notes on phone).

export type LandingPreference = 'schedule' | 'notes' | 'analytics' | 'last';

// When the landing preference is 'schedule', this sub-mode decides
// which schedule view is shown on first paint — Day, Week, or List.
// Stored alongside the main preference so the user can land directly
// in their preferred shape of the schedule.
export type ScheduleSubMode = 'list' | 'day' | 'week';

export const DEFAULT_LANDING: LandingPreference = 'schedule';
export const DEFAULT_SCHEDULE_SUBMODE: ScheduleSubMode = 'day';

const PREFERENCE_KEY = 'stacked.landing.preference';
const SUBMODE_KEY = 'stacked.landing.scheduleMode';
const LAST_PATH_KEY = 'stacked.landing.last';

const VALID: LandingPreference[] = ['schedule', 'notes', 'analytics', 'last'];
const VALID_SUBMODES: ScheduleSubMode[] = ['list', 'day', 'week'];

export function readLandingPreference(): LandingPreference {
  if (typeof window === 'undefined') return DEFAULT_LANDING;
  try {
    const raw = window.localStorage.getItem(PREFERENCE_KEY);
    if (raw && (VALID as string[]).includes(raw)) return raw as LandingPreference;
  } catch {
    // best-effort
  }
  return DEFAULT_LANDING;
}

export function writeLandingPreference(value: LandingPreference): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREFERENCE_KEY, value);
  } catch {
    // best-effort
  }
}

export function readScheduleSubMode(): ScheduleSubMode {
  if (typeof window === 'undefined') return DEFAULT_SCHEDULE_SUBMODE;
  try {
    const raw = window.localStorage.getItem(SUBMODE_KEY);
    if (raw && (VALID_SUBMODES as string[]).includes(raw)) return raw as ScheduleSubMode;
  } catch {
    // best-effort
  }
  return DEFAULT_SCHEDULE_SUBMODE;
}

export function writeScheduleSubMode(value: ScheduleSubMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SUBMODE_KEY, value);
  } catch {
    // best-effort
  }
}

export function readLastVisitedPath(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LAST_PATH_KEY);
    return raw && raw.startsWith('/') ? raw : null;
  } catch {
    return null;
  }
}

export function writeLastVisitedPath(path: string): void {
  if (typeof window === 'undefined') return;
  if (!path.startsWith('/')) return;
  try {
    window.localStorage.setItem(LAST_PATH_KEY, path);
  } catch {
    // best-effort
  }
}

// Resolve the preference into a concrete pathname. Returns null when
// the resolution lands on the schedule (the root URL) so callers can
// skip a redirect and render the page directly.
export function resolveLandingPath(
  preference: LandingPreference,
  lastVisited: string | null,
): string | null {
  switch (preference) {
    case 'schedule':
      return null;
    case 'notes':
      return '/notes';
    case 'analytics':
      return '/analytics';
    case 'last':
      // If the last-visited path is the schedule root, no redirect is
      // needed. Otherwise route to wherever the user left off.
      if (!lastVisited || lastVisited === '/') return null;
      return lastVisited;
  }
}
